import { Instant } from "@js-joda/core";
import { Lens } from "monocle-ts";
import * as API from "../../API";
import { InternetConnectionError } from "../../HttpRequest";
import { isLoading, Loading, loading } from "../../Loading";
import * as messages from "../../messages";
import { CaregiverId, ChatRoomId, PatientId } from "../../messages/ObjectIds";
import { genericUpdate } from "../../utils/generic_update";
import { HashMap } from "../../utils/HashMap";
import { KeyedList, List } from "../../utils/List";
import { memoize2 } from "../../utils/memoize";
import { memoizeAll2 } from "../../utils/memoizeAll";
import { reverseOrd } from "../../utils/OrdTuple";
import { EventSource } from "../EventSource";
import { KeyedOrderedActionProcessor, OrderedActionProcessor } from "../OrderedActionProcessor";

/**
 * The server method that is used to get the initial list of chat rooms
 */
export type ChatRefreshFn = (api: API.API) => Promise<messages.ChatRoom[]>;

export class ChatState {
  constructor(private readonly api: API.API, private readonly chatRefreshFn: ChatRefreshFn) {
    this.fullRefreshProcessor = new OrderedActionProcessor<messages.ChatRoom[]>(
      (payload: messages.ChatRoom[]) => {
        this.handleFullChatsReceived(payload);
      },
      (err: any) => {
        if (err instanceof InternetConnectionError) {
          return true;
        } else if (err instanceof API.ServerError) {
          return true;
        }
        console.warn("Unknown network error", err);
        return false;
      }
    );
    this.triggerFullRefresh();

    this.chatRefreshProcessor = new KeyedOrderedActionProcessor<ChatRoomId, messages.ChatRoom>(
      (key: ChatRoomId, payload: messages.ChatRoom) => {
        this.handleChatReceived(key, payload);
      },
      (err: any) => {
        if (err instanceof InternetConnectionError) {
          return true;
        } else if (err instanceof API.ServerError) {
          return true;
        }
        console.warn("Unknown network error", err);
        return false;
      }
    );
  }

  /**
   * When true, we can process chat refresh events immediately.
   *
   * When false, we are still waiting for a full refresh to complete, so we
   * have to queue chat refreshes, and trigger them when the full refresh completes.
   */
  private synced: boolean = false;

  private fullRefreshProcessor: OrderedActionProcessor<messages.ChatRoom[]>;
  private chatRefreshProcessor: KeyedOrderedActionProcessor<ChatRoomId, messages.ChatRoom>;

  private handleFullChatsReceived(payload: messages.ChatRoom[]): void {
    this.synced = true;

    this.queuedChatRefreshes.forEach((value: messages.ChatRoomUpdatedCaregiverPayload) => {
      this.refreshChatFromRealTimePush(value);
    });
    this.queuedChatRefreshes = new Set<messages.ChatRoomUpdatedCaregiverPayload>();

    // TODO remove found sendingMessages

    this.appChatState = {
      ...this.appChatState,
      chatRooms: HashMap.fromArray(
        payload.map((chatRoomMessage: messages.ChatRoom): [ChatRoomId, ChatRoom] => [
          chatRoomMessage.id,
          fromChatRoomMessage(chatRoomMessage),
        ])
      ),
    };

    this.onAppChatStateChanged.broadcast();
  }

  private handleChatReceived(key: ChatRoomId, payload: messages.ChatRoom): void {
    if (!this.synced) {
      // This is a result from a request that happened before a full refresh was triggered.
      // The full refresh nullifies the need for this now outdated result
      return;
    }

    if (isLoading(this.appChatState.chatRooms)) {
      throw new Error("The Impossible Happened");
    }

    function updateChatRoomSendingMessages(
      sendingMessages: List<SendingChatMessage> | undefined
    ): List<SendingChatMessage> | undefined {
      const chatRoom: messages.ChatRoom = payload;

      if (sendingMessages === undefined) {
        return undefined;
      }

      let updatedSendingMessages = sendingMessages;
      for (const message of chatRoom.messages) {
        updatedSendingMessages = updatedSendingMessages.filter(
          (x) => x.requestId !== message.requestId
        );
      }

      if (updatedSendingMessages.isEmpty()) {
        return undefined;
      } else {
        return updatedSendingMessages;
      }
    }

    const updatedChatRoom = fromChatRoomMessage(payload);

    this.appChatState = {
      chatRooms: this.appChatState.chatRooms.update((prevChatRoom: ChatRoom | undefined) => {
        if (prevChatRoom === undefined) {
          return updatedChatRoom;
        } else {
          return genericUpdate(prevChatRoom, updatedChatRoom);
        }
      }, key),
      sendingChatMessages: this.appChatState.sendingChatMessages.update(
        updateChatRoomSendingMessages,
        key
      ),
    };

    this.onAppChatStateChanged.broadcast();
  }

  public addSendingMessage(
    chatRoomId: ChatRoomId,
    requestId: string,
    payload: messages.ChatPayload
  ) {
    const message: SendingChatMessage = {
      requestId: requestId,
      payload: payload,
      status: {
        type: "Sending",
      },
    };

    this.appChatState = {
      ...this.appChatState,
      sendingChatMessages: this.appChatState.sendingChatMessages.update(
        (prev: List<SendingChatMessage> | undefined): List<SendingChatMessage> =>
          prev === undefined ? List.fromArray([message]) : prev.append(message),
        chatRoomId
      ),
    };

    this.onAppChatStateChanged.broadcast();
  }

  /**
   * Should be called after a reconnect, to account for events that might have been missed.
   */
  public triggerFullRefresh(): void {
    this.synced = false;
    this.fullRefreshProcessor.enqueue(() => {
      return this.chatRefreshFn(this.api);
    });
  }

  public refreshChatFromRealTimePush(
    chatRoomPayload: messages.ChatRoomUpdatedCaregiverPayload
  ): void {
    if (this.synced) {
      this.chatRefreshProcessor.enqueue(chatRoomPayload.chatRoomId, () => {
        return Promise.resolve(chatRoomPayload.chatRoomData);
      });
    } else {
      this.queuedChatRefreshes.add(chatRoomPayload);
    }
  }

  private queuedChatRefreshes = new Set<messages.ChatRoomUpdatedCaregiverPayload>();

  public readonly onAppChatStateChanged = new EventSource<void>();

  private appChatState: AppChatState = {
    chatRooms: loading(),
    sendingChatMessages: HashMap.empty(),
  };

  public getAppChatState(): AppChatState {
    return this.appChatState;
  }

  /**
   * Use this for sending a chat to a chat room where you do not
   * yet have a ChatRoomId
   */
  public async createChatRoom(createAction: () => Promise<messages.ChatRoom>): Promise<ChatRoom> {
    const chatRoomMessage = await createAction();
    const chatRoomId = chatRoomMessage.id;
    // Check if in the meanwhile we have already recieved a more recent snapshot
    // of the Chat Room we just created
    const existingChatRoom = AppChatState.getChatRoom(this.appChatState, chatRoomId);
    if (existingChatRoom !== null) {
      return existingChatRoom;
    } else {
      const chatRoom = fromChatRoomMessage(chatRoomMessage);
      this.appChatState = AppChatState.setChatRoom(this.appChatState, chatRoomId, chatRoom);
      return chatRoom;
    }
  }

  private closed = false;
  public close(): void {
    if (this.closed) {
      throw new Error("AppState already closed");
    }
    this.closed = true;

    this.fullRefreshProcessor.close();
    this.chatRefreshProcessor.close();
  }
}

export interface AppChatState {
  readonly chatRooms: HashMap<ChatRoomId, ChatRoom> | Loading;
  readonly sendingChatMessages: HashMap<ChatRoomId, List<SendingChatMessage>>;
}

export class AppChatStateLenses {
  static chatRooms = Lens.fromProp<AppChatState>()("chatRooms");
}

export interface LatestChatSummary {
  readonly chatRoomId: ChatRoomId;
  readonly chatRoomType: messages.ChatRoomType;
  readonly latestChatMessage: messages.ChatRoomMessage;
  readonly numNewMessages: number;
  readonly otherSeen: boolean;
}

export interface ChatSystemStrategies {
  numNewMessagesStrategy: (chatRoom: ChatRoom) => number;
  otherSeenStrategy: (chatRoom: ChatRoom) => boolean;
}

export interface ChatRoom {
  readonly id: ChatRoomId;
  readonly chatRoomType: messages.ChatRoomType;
  readonly members: KeyedList<messages.ChatRoomMember, "user">;
  readonly messages: KeyedList<messages.ChatRoomMessage, "requestId">;
}

export interface SendingChatMessage {
  requestId: string;
  payload: messages.ChatPayload;
  status: SendingChatMessageStatus;
}

export type SendingChatMessageStatus =
  | SendingChatMessageStatus.Sending
  | SendingChatMessageStatus.Error;

export declare namespace SendingChatMessageStatus {
  export interface Sending {
    type: "Sending";
  }

  export interface Error {
    type: "Error";
    error: Error;
  }
}

export function fromChatRoomMessage(chatRoomMessage: messages.ChatRoom): ChatRoom {
  return {
    id: chatRoomMessage.id,
    chatRoomType: chatRoomMessage.chatRoomType,
    members: KeyedList.from(chatRoomMessage.members)("user"),
    messages: KeyedList.from(chatRoomMessage.messages)("requestId"),
  };
}

/*
function updateChatRoom(old: ChatRoom, updated: messages.ChatRoom): ChatRoom {
    if (deepEqual<messages.ChatRoom>(updated, {
        id: old.id,
        chatRoomType: old.chatRoomType,
        members: old.members.toArray(),
        messages: old.messages.toArray()
    })) {
        return old;
    }
    return {
        id: updated.id,
        chatRoomType: updateChatRoomType(old.chatRoomType, updated.chatRoomType),
        members: null as any, // TODO !!!!!!!!!!!!!!!!
        messages: null as any, // TODO !!!!!!!!!!!!!!!!!
    }
}
*/

// function updateChatRoomType(old: ChatRoomType, :

export class AppChatState {
  static setChatRoom(
    appChatState: AppChatState,
    chatRoomId: ChatRoomId,
    chatRoom: ChatRoom
  ): AppChatState {
    if (isLoading(appChatState.chatRooms)) {
      // TODO ???? Looks like we have a flawed architecture...
      //
      // what does indeed happen when a chat room is created while the chat rooms are being loaded?
      return appChatState;
    }

    return {
      ...appChatState,
      chatRooms: appChatState.chatRooms.set(chatRoomId, chatRoom),
    };
  }

  static getChatRoom(appChatState: AppChatState, chatRoomId: ChatRoomId): ChatRoom | null {
    if (isLoading(appChatState.chatRooms)) {
      return null;
    }

    const result = appChatState.chatRooms.get(chatRoomId);
    if (result === undefined) {
      return null;
    } else {
      return result;
    }
  }

  static numNewChatMessages: (
    appChatState: AppChatState,
    chatSystemStrategies: ChatSystemStrategies
  ) => number = memoize2(AppChatState.numNewChatMessagesImpl);
  private static numNewChatMessagesImpl(
    appChatState: AppChatState,
    chatSystemStrategies: ChatSystemStrategies
  ): number {
    if (isLoading(appChatState.chatRooms)) {
      return 0;
    }

    let count = 0;
    for (const chatRoom of appChatState.chatRooms.values()) {
      if (chatSystemStrategies.numNewMessagesStrategy(chatRoom) > 0) {
        count += 1;
      }
    }

    return count;
  }

  static getLatestChats: (
    appChatState: AppChatState,
    chatSystemStrategies: ChatSystemStrategies
  ) => List<LatestChatSummary> | Loading = memoize2(AppChatState.getLatestChatsImpl);

  private static getLatestChatsImpl(
    appChatState: AppChatState,
    chatSystemStrategies: ChatSystemStrategies
  ): List<LatestChatSummary> | Loading {
    if (isLoading(appChatState.chatRooms)) {
      return loading();
    }

    let result: List<LatestChatSummary> = List.empty();

    for (const chatRoom of appChatState.chatRooms.values()) {
      const latestChat = AppChatState.getLatestChat(chatRoom, chatSystemStrategies);
      if (latestChat !== null) {
        result = result.append(latestChat);
      }
    }

    return result.sortBy((x) => reverseOrd(x.latestChatMessage.createdAt));
  }

  private static getLatestChat: (
    chatRoom: ChatRoom,
    chatSystemStrategies: ChatSystemStrategies
  ) => LatestChatSummary | null = memoizeAll2(AppChatState.getLatestChatImpl);

  private static getLatestChatImpl(
    chatRoom: ChatRoom,
    chatSystemStrategies: ChatSystemStrategies
  ): LatestChatSummary | null {
    const lastMessage = chatRoom.messages.last();
    if (lastMessage === undefined) {
      return null;
    }

    return {
      chatRoomId: chatRoom.id,
      chatRoomType: chatRoom.chatRoomType,
      latestChatMessage: lastMessage,
      numNewMessages: chatSystemStrategies.numNewMessagesStrategy(chatRoom),
      otherSeen: chatSystemStrategies.otherSeenStrategy(chatRoom),
    };
  }

  private static patientNumNewChatMessagesImpl = (
    appChatState: AppChatState,
    patientId: PatientId
  ): number => {
    if (isLoading(appChatState.chatRooms)) {
      return 0;
    }

    for (const chatRoom of appChatState.chatRooms.values()) {
      if (chatRoom.chatRoomType.type === "CaregiverPatientChat") {
        if (chatRoom.chatRoomType.patientId === patientId) {
          return caregiverNumNewMessages(chatRoom, chatRoom.chatRoomType.caregiverId);
        }
      }
    }

    return 0;
  };

  static patientNumNewChatMessages = memoize2(AppChatState.patientNumNewChatMessagesImpl);
}

function caregiverNumNewMessages(chatRoom: ChatRoom, caregiverId: CaregiverId) {
  let count = 0;

  let lastViewed: Instant = Instant.MIN;
  for (const m of chatRoom.members) {
    if (m.user.type === "Caregiver" && m.user.caregiverId === caregiverId) {
      lastViewed = m.lastViewed;
      break;
    }
  }

  for (const msg of chatRoom.messages.backwards()) {
    if (!lastViewed.isBefore(msg.createdAt)) {
      return count;
    }
    if (!(msg.user.type === "Caregiver" && msg.user.caregiverId === caregiverId)) {
      count += 1;
    }
  }

  return count;
}

export function caregiverChatStrategies(caregiverId: CaregiverId): ChatSystemStrategies {
  return {
    numNewMessagesStrategy: (chatRoom: ChatRoom): number => {
      return caregiverNumNewMessages(chatRoom, caregiverId);
    },
    otherSeenStrategy: (_chatRoom: ChatRoom): boolean => {
      // Caregiver is not allowed to know the "seen" status of the messages that she sends

      return false;
    },
  };
}
